import { useState } from "react";
import { X, FileText, User } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

interface AddConsentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onSave?: (data: any) => void;
}

const consentForms = [
  { id: "botox-consent", name: "Botox Consent Form" },
  { id: "dermal-consent", name: "Dermal Filler Consent Form" },
  { id: "lip-consent", name: "Lip Filler Consent Form" },
  { id: "chemical-consent", name: "Chemical Peel Consent Form" },
  { id: "microneedling-consent", name: "Microneedling Consent Form" },
  { id: "general-consent", name: "General Treatment Consent" },
];

const treatmentTypes = [
  { id: "botox", name: "Botox treatments" },
  { id: "dermal", name: "Dermal fillers" },
  { id: "lip-filler", name: "Lip Filler" },
  { id: "chemical-peel", name: "Chemical Peel" },
  { id: "microneedling", name: "Microneedling" },
];

export default function AddConsentFormModal({
  isOpen,
  onClose,
  client,
  onSave,
}: AddConsentFormModalProps) {
  const [selectedConsent, setSelectedConsent] = useState("");
  const [linkedTreatment, setLinkedTreatment] = useState("");
  const [notes, setNotes] = useState("");
  const [linkToFuture, setLinkToFuture] = useState(false);

  const handleSave = () => {
    const data = {
      consentForm: selectedConsent,
      linkedTreatment,
      notes,
      linkToFuture,
    };
    onSave?.(data);
    // Reset form
    setSelectedConsent("");
    setLinkedTreatment("");
    setNotes("");
    setLinkToFuture(false);
    onClose();
  };

  const isValid = selectedConsent;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">Add Consent Form</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Client Info */}
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {client.avatar ? (
                <img src={client.avatar} alt={client.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#704aaf]">{client.name}</p>
              <p className="text-xs text-[#6b6b6b]">{client.email}</p>
            </div>
          </div>

          {/* Consent Form Select */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Consent Form
            </label>
            <Select value={selectedConsent} onValueChange={setSelectedConsent}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#704aaf]" />
                  <SelectValue placeholder="Select a consent form..." />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {consentForms.map((form) => (
                  <SelectItem key={form.id} value={form.id}>
                    {form.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Link to Treatment */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Link to Treatment
            </label>
            <Select value={linkedTreatment} onValueChange={setLinkedTreatment}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <SelectValue placeholder="Optional: Link this consent to a treatment..." />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {treatmentTypes.map((treatment) => (
                  <SelectItem key={treatment.id} value={treatment.id}>
                    {treatment.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any relevant notes here..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all bg-[#faf8fc]"
            />
          </div>

          {/* Link to Future Treatments */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="linkToFuture"
              checked={linkToFuture}
              onChange={(e) => setLinkToFuture(e.target.checked)}
              className="mt-1 w-4 h-4 text-[#704aaf] border-gray-300 rounded focus:ring-purple-200"
            />
            <label htmlFor="linkToFuture" className="text-sm">
              <span className="font-medium text-[#2d2d2d]">Link to Future Treatments</span>
              <p className="text-xs text-[#6b6b6b] mt-0.5">
                Automatically attach this consent form to future treatments.
              </p>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Consent Form
              <span className="text-white/80">›</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}